import TaskDialogV2, {
  type TaskDialogMode,
} from '@/components/deal/tasks/task-dialog-v2.tsx';
import { TasksFractionV2 } from '@/components/deal/tasks/tasks-fraction.tsx';
import { TasksProgressRingV2 } from '@/components/deal/tasks/tasks-progress-ring.tsx';
import { Button } from '@/components/ui/button.tsx';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover.tsx';
import { CommandGroup, CommandItem } from '@/components/ui/command.tsx';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon.tsx';
import { PDKDraftingIcon } from '@/components/ui/pdk-icons.tsx';
import HotKeyListener from '@/components/ux/hot-key-listener.tsx';
import {
  useAssignTaskMutation,
  useAssignTaskMutationV2,
} from '@/hooks/use-assign-task.ts';
import { useCreateTaskTemplateMutation } from '@/hooks/use-create-task.ts';
import { cn } from '@/lib/utils.ts';
import { taskStoreV2, taskTemplateStore } from '@/models/stores/tasks-v2.ts';
import { TaskableType } from '@/models/types.ts';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { Landmark, Plus } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

// uses generalized tasks
const GeneralTaskSelectorV2 = observer(
  ({
    taskableType,
    taskableId,
    withHotkeyListener = false,
    hotkey,
  }: {
    taskableType: TaskableType;
    taskableId: string;
    withHotkeyListener?: boolean;
    hotkey?: string | [string, string];
  }) => {
    const assignedTasks = taskStoreV2.getByTarget(taskableId, taskableType);
    const taskTemplates =
      taskTemplateStore.getTaskTemplatesByTaskableType(taskableType);
    const [dialogIsOpen, setDialogIsOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<TaskDialogMode>('one-time');
    const [comboBoxPopoverIsOpen, setComboBoxPopoverIsOpen] = useState(false);
    const [saveAsTemplate, setSaveAsTemplate] = useState(false);
    const [addToCurrentEntity, setAddToCurrentEntity] = useState(true);

    const taskOptions = taskTemplates.map((taskTemplate) => ({
      value: taskTemplate.id,
      id: taskTemplate.id,
      label: taskTemplate.title,
      description: taskTemplate.description,
      icon: <Landmark className="h-3.5 w-3.5" />,
      searchTerms: [taskTemplate.title, taskTemplate.description || ''],
    }));

    const queryClient = useQueryClient();

    const [titleValue, setTitleValue] = useState('');
    const [descriptionValue, setDescriptionValue] = useState('');

    const createTaskTemplateMutation = useCreateTaskTemplateMutation();
    const assignTaskMutation = useAssignTaskMutationV2();
    // TODO -- remove later
    const oldAssignTaskMutation = useAssignTaskMutation();

    const handleCreateTaskSuccess = () => {
      setDialogIsOpen(false);
      setTitleValue('');
      setDescriptionValue('');
      setSaveAsTemplate(false);
      setAddToCurrentEntity(true);
    };

    const handleAssignTask = (value: string) => {
      const taskTemplate = taskTemplates.find((task) => task.id === value);
      if (!taskTemplate) {
        return;
      } else {
        assignTaskMutation.mutate({
          task: {
            id: uuidv4(),
            task_template_id: taskTemplate.id,
            taskable_type: taskableType,
            taskable_id: taskableId,
            title: taskTemplate.title,
            description: taskTemplate.description,
            status: 'incomplete',
          },
          taskableId: taskableId,
          taskableType: taskableType,
        });
      }
    };

    const handleCreateTask = async () => {
      if (dialogMode === 'one-time') {
        const assignedTaskId = uuidv4();
        assignTaskMutation.mutate({
          task: {
            id: assignedTaskId,
            task_template_id: null,
            taskable_type: taskableType,
            taskable_id: taskableId,
            title: titleValue,
            description: descriptionValue,
            status: 'incomplete',
          },
          taskableId: taskableId,
          taskableType: taskableType,
        });

        if (saveAsTemplate) {
          const taskTemplateId = uuidv4();
          await createTaskTemplateMutation.mutateAsync({
            taskTemplate: {
              id: taskTemplateId,
              title: titleValue,
              description: descriptionValue,
              taskable_type: taskableType,
            },
            taskableType: taskableType,
          });

          // TODO -- remove later
          oldAssignTaskMutation.mutate({
            task: {
              id: assignedTaskId,
              task_id: taskTemplateId,
              deal_id: taskableId,
              title: titleValue,
              description: descriptionValue,
              status: 'incomplete',
            },
            dealId: taskableId,
          });
        }

        handleCreateTaskSuccess();
      } else {
        // create template
        const templateId = uuidv4();
        createTaskTemplateMutation.mutate(
          {
            taskTemplate: {
              id: templateId,
              title: titleValue,
              description: descriptionValue,
              taskable_type: taskableType,
            },
            taskableId: addToCurrentEntity ? taskableId : undefined,
            taskableType: taskableType,
          },
          {
            onSuccess: handleCreateTaskSuccess,
            onSettled: () => {
              queryClient.invalidateQueries({ queryKey: ['tasks'] });
            },
          },
        );
      }
    };

    const hasAssignedTasks = !isEmpty(assignedTasks);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const onHotkeyPressed = useCallback(() => {
      buttonRef.current?.click();
    }, []);

    return (
      <div
        className={cn('flex flex-row gap-2 w-full px-2', {
          'justify-between': hasAssignedTasks,
        })}
      >
        {hasAssignedTasks && (
          <div className="flex flex-row gap-2 items-center">
            <div className="text-xs text-gray-500 text-nowrap">Tasks</div>
            <TasksFractionV2 assignedTasks={assignedTasks} />
            <TasksProgressRingV2 assignedTasks={assignedTasks} />
          </div>
        )}
        <div>
          <ComboboxPopover
            open={comboBoxPopoverIsOpen}
            onOpenChange={setComboBoxPopoverIsOpen}
            endAdornment={
              hotkey ? <KeyboardKeyIcon>{hotkey}</KeyboardKeyIcon> : undefined
            }
            placeholder="Set task..."
            onChange={handleAssignTask}
            emptyMessage="No task found"
            options={taskOptions}
            footerOptions={
              <CommandGroup>
                <p className="px-2 pt-2 pb-1 text-xs text-gray-400 font-medium">
                  New task
                </p>
                <CommandItem
                  onSelect={() => {
                    setDialogMode('one-time');
                    setComboBoxPopoverIsOpen(false);
                    setDialogIsOpen(true);
                  }}
                >
                  <PDKDraftingIcon />
                  <p className="text-xs text-gray-600">Create one-time task</p>
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    setDialogMode('template');
                    setComboBoxPopoverIsOpen(false);
                    setDialogIsOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  <p className="text-xs text-gray-600">Create new template</p>
                </CommandItem>
              </CommandGroup>
            }
            side={hasAssignedTasks ? 'left' : 'right'}
            align="start"
          >
            <div>
              <Button
                variant="ghost"
                className="px-0 py-1 h-fit"
                ref={buttonRef}
              >
                {!hasAssignedTasks ? (
                  <div className="flex flex-row gap-1 items-center">
                    <Plus
                      className={cn(
                        'h-3.5 w-3.5 cursor-pointer text-gray-500',
                        {},
                      )}
                    />
                    <div className="text-xs text-gray-500 text-nowrap">
                      Add task
                    </div>
                  </div>
                ) : (
                  <Plus
                    className={cn(
                      'h-3.5 w-3.5 cursor-pointer text-gray-500',
                      {},
                    )}
                  />
                )}
              </Button>

              {withHotkeyListener && hotkey && (
                <HotKeyListener
                  hotkeys={hotkey}
                  onHotkeyPressed={onHotkeyPressed}
                />
              )}
            </div>
          </ComboboxPopover>

          <TaskDialogV2
            mode={dialogMode}
            taskableType={taskableType}
            titleValue={titleValue}
            setTitleValue={setTitleValue}
            descriptionValue={descriptionValue}
            setDescriptionValue={setDescriptionValue}
            saveAsTemplate={saveAsTemplate}
            setSaveAsTemplate={setSaveAsTemplate}
            addToCurrentEntity={addToCurrentEntity}
            setAddToCurrentEntity={setAddToCurrentEntity}
            onClickCta={handleCreateTask}
            ctaText={
              dialogMode === 'one-time' ? 'Create Task' : 'Create Template'
            }
            onOpen={() => setDialogIsOpen(true)}
            onClose={() => setDialogIsOpen(false)}
            dialogIsOpen={dialogIsOpen}
          />
        </div>
      </div>
    );
  },
);

export default GeneralTaskSelectorV2;
